import {
  circle,
  ellipse,
  fill,
  flat,
  line,
  P,
  poly,
  polyline,
  rect,
  shine,
  type IconDef,
  type Shape,
} from '../kit';

/** Where a drawn group goes: mirrored, scaled by `s`, turned `deg` clockwise about (cx, cy), then shifted. */
interface Move {
  deg?: number;
  s?: number;
  cx?: number;
  cy?: number;
  dx?: number;
  dy?: number;
  flip?: boolean;
}

const r2 = (v: number): number => Math.round(v * 100) / 100;
const ARGS: Record<string, number> = { m: 2, l: 2, h: 1, v: 1, c: 6, q: 4, a: 7, z: 0 };

/** Moves path data (M L H V C Q A Z, absolute or relative); the result is absolute. */
function xf(d: string, m: Move): string {
  const { deg = 0, s = 1, cx = 16, cy = 16, dx = 0, dy = 0, flip = false } = m;
  const t = (deg * Math.PI) / 180;
  const co = Math.cos(t);
  const si = Math.sin(t);
  const pt = (x: number, y: number): string => {
    const u = (flip ? cx - x : x - cx) * s;
    const v = (y - cy) * s;
    return `${r2(cx + u * co - v * si + dx)} ${r2(cy + u * si + v * co + dy)}`;
  };
  const tok = d.match(/[a-zA-Z]|-?(?:\d+\.?\d*|\.\d+)/g) ?? [];
  let out = '';
  let [x, y, x0, y0] = [0, 0, 0, 0];
  let cmd = 'M';
  for (let i = 0; i < tok.length;) {
    if (/[a-zA-Z]/.test(tok[i] as string)) cmd = tok[i++] as string;
    const c = cmd.toLowerCase();
    const k = ARGS[c] ?? 0;
    const a = tok.slice(i, i + k).map(Number);
    i += k;
    const [ox, oy] = cmd === c ? [x, y] : [0, 0];
    if (c === 'z') {
      out += 'Z';
      [x, y] = [x0, y0];
    } else if (c === 'h') {
      x = ox + (a[0] as number);
      out += `L${pt(x, y)}`;
    } else if (c === 'v') {
      y = oy + (a[0] as number);
      out += `L${pt(x, y)}`;
    } else if (c === 'a') {
      const [rx, ry, rot, big, sweep, ex, ey] = a as [number, number, number, number, number, number, number];
      x = ox + ex;
      y = oy + ey;
      out += `A${r2(rx * s)} ${r2(ry * s)} ${r2((flip ? -rot : rot) + deg)} ${big} ${flip ? 1 - sweep : sweep} ${pt(x, y)}`;
    } else {
      const ps: string[] = [];
      for (let j = 0; j < k; j += 2) ps.push(pt(ox + (a[j] as number), oy + (a[j + 1] as number)));
      x = ox + (a[k - 2] as number);
      y = oy + (a[k - 1] as number);
      out += c.toUpperCase() + ps.join(' ');
      if (c === 'm') {
        [x0, y0] = [x, y];
        cmd = cmd === 'm' ? 'l' : 'L';
      }
    }
  }
  return out;
}

/** A group of shapes, moved. */
const moved = (shapes: readonly Shape[], m: Move): Shape[] => shapes.map((sh) => ({ ...sh, d: xf(sh.d, m) }));

/** Numbers from a list like "x0 y0, x1 y1, ...". */
const nums = (s: string): number[] =>
  s
    .trim()
    .split(/[\s,]+/)
    .map(Number);

/** "x0 y0, x1 y1, ..." as [x, y] pairs. */
const pairs = (s: string): [number, number][] => {
  const v = nums(s);
  return Array.from({ length: v.length / 2 }, (_, i) => [v[2 * i] as number, v[2 * i + 1] as number]);
};

/** A smooth closed curve through the points ("x0 y0, x1 y1, ..." or a flat list of numbers). */
function smooth(points: string | number[]): string {
  const pts = typeof points === 'string' ? nums(points) : points;
  const n = pts.length / 2;
  const X = (i: number): number => pts[((i + n) % n) * 2] as number;
  const Y = (i: number): number => pts[((i + n) % n) * 2 + 1] as number;
  let d = `M${r2(X(0))} ${r2(Y(0))}`;
  for (let i = 0; i < n; i++) {
    d +=
      `C${r2(X(i) + (X(i + 1) - X(i - 1)) / 6)} ${r2(Y(i) + (Y(i + 1) - Y(i - 1)) / 6)} ` +
      `${r2(X(i + 1) - (X(i + 2) - X(i)) / 6)} ${r2(Y(i + 1) - (Y(i + 2) - Y(i)) / 6)} ${r2(X(i + 1))} ${r2(Y(i + 1))}`;
  }
  return `${d}Z`;
}

/** A circle wound the other way round from kit's `circle`: inside a filled circle it cuts a hole. */
const hole = (cx: number, cy: number, r: number): string =>
  `M${r2(cx - r)} ${r2(cy)}a${r} ${r} 0 1 1 ${r2(2 * r)} 0a${r} ${r} 0 1 1 ${r2(-2 * r)} 0Z`;

/** A Nattie Lite can like :beer: (gold, blue band, white stripe), w x h with its corner at (x, y). */
function can(x: number, y: number, w: number, h: number): Shape[] {
  const b = y + h * 0.27;
  const bh = h * 0.4;
  return [
    fill(P.gold, rect(x, y, w, h, Math.min(2.4, w * 0.22))),
    flat(P.blue, rect(x + 0.8, b, w - 1.6, bh)),
    flat(P.white, rect(x + 0.8, b + bh * 0.38, w - 1.6, Math.max(1.2, bh * 0.21))),
    shine(rect(x + 1.5, y + 1.4, 1.5, h - 2.8, 0.75), 0.45),
  ];
}

/** Same-shaped groups painted layer by layer: every group's first shape as one, then every second... */
const layered = (groups: Shape[][]): Shape[] =>
  (groups[0] ?? []).map((sh, i) => ({ ...sh, d: groups.map((g) => g[i]?.d ?? '').join('') }));

/** A can with a head of foam, centered on (16, 16), for :cheers:. */
const foamyCan: Shape[] = [
  ...can(11.5, 8, 9, 16),
  fill(
    P.foam,
    'M11.2 10.4C10.2 8 12.4 6 14.2 7C14.8 4.6 18.2 4.6 18.6 6.8C20.4 6 22 7.8 20.8 10.2C20.2 11.2 19 11 18.6 10.2C18 11.2 16.6 11.2 16 10.2C15.4 11.2 14 11.2 13.6 10.2C13 11.2 11.6 11.2 11.2 10.4Z',
  ),
];

/** A mason jar's glass, and its metal ring lid. */
const MASON =
  'M9.6 8.4V10.2C9.6 11.4 6.6 12 6.6 14.6V25.8C6.6 27.8 7.8 29 9.8 29H22.2C24.2 29 25.4 27.8 25.4 25.8V14.6C25.4 12 22.4 11.4 22.4 10.2V8.4Z';
const masonLid: Shape[] = [
  fill(P.lightGray, rect(8.2, 3.2, 15.6, 6.2, 1.6)),
  line('M9.8 6.4H22.2', 1.2, P.gray),
];

/** A fried drumstick, bone up, the meat centered on (16, 16). */
const drumstick: Shape[] = [
  fill(
    P.cream,
    'M15 12V8.2C13.4 8.4 12.8 6 14.4 5.4C15 5.2 15.6 5.4 16 6C16.4 5.4 17 5.2 17.6 5.4C19.2 6 18.6 8.4 17 8.2V12Z',
  ),
  fill(
    P.amber,
    'M14.6 10.8C11.2 11.6 10.6 15 11.2 17.6C11.8 20.2 13.6 21.4 16 21.4C18.4 21.4 20.2 20.2 20.8 17.6C21.4 15 20.8 11.6 17.4 10.8C16.4 10.6 15.6 10.6 14.6 10.8Z',
  ),
  line('M13.4 14.6L14.6 15.2M17.4 13.6L18.4 13M15.6 18L16.8 18.6', 1.2, P.wood),
];

/** A puffy pork rind about 7k x 5k, centered on (cx, cy). */
const rind = (cx: number, cy: number, k = 1): string =>
  smooth(
    nums('-3.4 0.8, -2.6 -1.8, -0.4 -1.4, 1.2 -2.6, 3.2 -1, 3 1.6, 0.6 2.2, -1.8 2.4').map(
      (v, i) => (i % 2 ? cy : cx) + v * k,
    ),
  );

/** A peanut in the shell, standing up, centered on (16, 16). */
const peanut: Shape[] = [
  fill(
    P.tan,
    'M16 7.2C18.6 7.2 20 9.2 20 11.6C20 13.6 19 14.6 19 15.8C19 17 20.4 18 20.4 20.2C20.4 23 18.6 24.8 16 24.8C13.4 24.8 11.6 23 11.6 20.2C11.6 18 13 17 13 15.8C13 14.6 12 13.6 12 11.6C12 9.2 13.4 7.2 16 7.2Z',
  ),
  line(
    'M14.4 9.6C14 11 14.2 12.4 14.8 13.4M17.6 9.6C18 11 17.8 12.4 17.2 13.4M14.2 18.4C13.8 20 14 21.6 14.8 22.6M17.8 18.4C18.2 20 18 21.6 17.2 22.6',
    1.2,
    P.wood,
  ),
];

/** One twisted end of a candy wrapper, on the left. */
const WRAPPER_END =
  'M10.4 14.4L5.2 9.6L4.2 11.4L2.8 12L3.6 14.2L2.4 16L3.6 17.8L2.8 20L4.2 20.6L5.2 22.4L10.4 17.6Z';

/** An ice cube seen from above at an angle, size s, centered on (cx, cy). */
function cube(cx: number, cy: number, s: number): Shape[] {
  const [h, q] = [s * 0.5, s * 0.28];
  const t = cy - s * 0.56;
  const b = cy + s * 0.56;
  return [
    fill(P.glass, poly(cx, t, cx + h, cy - q, cx + h, cy + q, cx, b, cx - h, cy + q, cx - h, cy - q)),
    flat(P.white, poly(cx, t + 1, cx + h - 1.4, cy - q, cx, cy - 0.8, cx - h + 1.4, cy - q), 0.8),
    flat(
      P.sky,
      poly(cx + 0.6, cy + 0.4, cx + h - 0.8, cy - q + 0.8, cx + h - 0.8, cy + q - 0.4, cx + 0.6, b - 1),
      0.6,
    ),
    line(polyline(cx - h, cy - q, cx, cy, cx + h, cy - q) + polyline(cx, cy, cx, b), 1.2),
    line(polyline(cx - h + 1.6, cy - q + 2.2, cx - h + 1.6, cy + q - 0.4), 1.4, '#ffffff', 0.8),
  ];
}

export const food = {
  /** A cold one: a gold can, cracked, foam over the top. */
  beer: [
    fill(P.gold, rect(9, 8, 14, 20.5, 2.5)),
    flat(P.blue, rect(9.8, 13.2, 12.4, 8.4)),
    flat(P.white, rect(9.8, 16.4, 12.4, 1.8)),
    shine(rect(11.2, 9.6, 2, 17.2, 1), 0.45),
    fill(
      P.foam,
      'M8.6 9.4C7.6 6.4 10.6 4.6 12.4 6C13.2 3.6 17.4 3.4 18.2 5.8C20.4 4.4 23.6 6.2 23.2 9C23.1 10.2 22.6 11.4 22.6 12.6C22.6 13.6 21.2 13.6 21.2 12.4C21.2 11.4 21 10.6 20.4 10.2C19.8 11 18.6 11 18 10.2C17.2 11.1 15.8 11.1 15.2 10.2C14.4 11.2 12.6 11.3 12 10.2C11.2 11.4 9 11 8.6 9.4Z',
    ),
  ],
  /** A roller-grill dog with a stripe of mustard. */
  hotdog: [
    fill(P.bun, rect(5, 10, 22, 8, 4)),
    fill(P.meat, rect(3, 13, 26, 6, 3)),
    shine(rect(7, 13.9, 9, 1.2, 0.6), 0.4),
    fill(P.bun, 'M4.6 17.4H27.4C27.4 22.2 24.6 24.6 21.4 24.6H10.6C7.4 24.6 4.6 22.2 4.6 17.4Z'),
    line(
      polyline(7, 15.8, 9.5, 14.2, 12, 15.8, 14.5, 14.2, 17, 15.8, 19.5, 14.2, 22, 15.8, 24.5, 14.2),
      1.5,
      P.mustard,
    ),
  ],
  /** Cheers: two foamy Nattie Lite cans tilted together, clinking at the top. */
  cheers: [
    ...moved(foamyCan, { deg: 17, dx: -6.4, dy: 3.6 }),
    ...moved(foamyCan, { deg: -17, dx: 6.4, dy: 3.6 }),
    line('M16 2.6V5M11.6 4L13 6M20.4 4L19 6', 1.6),
  ],
  /** A cardboard 12-pack: a blue-and-white box at an angle, gold can tops showing at the open top. */
  'beer-case': moved(
    [
      fill(P.navy, poly(3, 15, 21.6, 15, 28.2, 10, 9.6, 10)),
      fill(
        P.gold,
        [9.8, 15.6, 21.4].map((x) => `M${x - 2.8} 16V10A2.8 1.2 0 0 1 ${x + 2.8} 10V16Z`).join(''),
      ),
      fill(P.silver, [9.8, 15.6, 21.4].map((x) => ellipse(x, 10, 2.8, 1.2)).join(''), { w: 1.2 }),
      fill('#2b5fb4', poly(21.6, 15, 28.2, 10, 28.2, 23.8, 21.6, 28.8)),
      flat(P.white, poly(22.4, 19, 27.4, 15.2, 27.4, 18.8, 22.4, 22.6)),
      fill(P.blue, rect(3, 15, 18.6, 13.8, 0.6)),
      flat(P.white, rect(3.8, 19.6, 17, 3.6)),
    ],
    { dy: -3 },
  ),
  /** An empty can for recycling: silver, crushed in the middle, a little tilted. */
  'empty-can': moved(
    [
      fill(
        P.silver,
        'M6.94 10.4C5.88 8.03 16.85 3.15 17.9 5.52L21.32 13.19L20.2 15L22.6 16.2L21.2 17.6L22 18.8V25.4C22 28 10 28 10 25.4V21L12.4 19.4L9.8 18L11.8 16.8L9.4 15.6Z',
      ),
      flat(P.blue, rect(10.8, 21.2, 10.4, 3)),
      line('M6.94 10.4C8 12.78 18.96 7.9 17.9 5.52', 1.2),
      line(
        polyline(12.4, 19.4, 14.4, 17.8, 16.4, 18.8, 18.6, 17, 21.2, 17.6) +
          polyline(11.8, 16.8, 14, 16.2, 15.6, 14.4, 18, 15.6, 20.2, 15),
        1.3,
        P.gray,
      ),
      line('M10.27 11.98L11.74 15.27M12.4 21.6V25', 1.6, '#ffffff', 0.8),
      flat(P.gray, xf(ellipse(17.2, 6.8, 1.6, 0.7), { deg: -24 })),
    ],
    { deg: 14, s: 1.1, dx: 0.6, dy: -0.4 },
  ),
  /** A pyramid of six Nattie Lite cans stacked 3-2-1. */
  'can-pyramid': layered(
    pairs('4.3 20.4, 12.1 20.4, 19.9 20.4, 8.2 11.6, 16 11.6, 12.1 2.8').map(([x, y]) => can(x, y, 7.8, 8.8)),
  ),
  /** A "shooter" mini bottle of cinnamon whiskey: amber, red cap, red label with a little flame. */
  'fire-bawl': [
    fill(
      P.glass,
      'M13.4 7H18.6V9.4C18.6 10.6 19.6 11 21.2 11.4C23.2 11.9 24.4 13.2 24.4 15.4V26C24.4 27.8 23.2 29 21.4 29H10.6C8.8 29 7.6 27.8 7.6 26V15.4C7.6 13.2 8.8 11.9 10.8 11.4C12.4 11 13.4 10.6 13.4 9.4Z',
    ),
    flat(
      P.rust,
      'M8.4 15.4C8.4 13.8 9.2 13 10.8 12.6H21.2C22.8 13 23.6 13.8 23.6 15.4V26C23.6 27.4 22.8 28.2 21.4 28.2H10.6C9.2 28.2 8.4 27.4 8.4 26Z',
    ),
    flat(P.red, rect(8.4, 16.2, 15.2, 8.8)),
    flat(
      P.yellow,
      xf(
        'M16 29C9.5 29 6 24.6 6 19.6C6 14 10.5 11.4 11.2 6.6C14.4 8.6 15.6 11.4 15.4 14.2C17 12.6 17.6 9.4 17 5.6C22.6 8.6 26 14 26 19.8C26 25 22.4 29 16 29Z',
        { s: 0.27, cy: 17.3, dy: 3.3 },
      ),
    ),
    shine(rect(9.6, 13.6, 1.6, 1.8, 0.8), 0.6),
    shine(rect(14.4, 7.8, 1.2, 3, 0.6), 0.6),
    fill(P.red, rect(12.6, 2.4, 6.8, 4.6, 1.2)),
    line('M14.6 3.6V5.8M16 3.6V5.8M17.4 3.6V5.8', 1.2, P.darkRed),
  ],
  /** A short tumbler of amber whiskey with an ice cube. */
  whiskey: moved(
    [
      fill(P.glass, 'M4.6 9H27.4L25.6 26.4C25.4 28 24.4 28.8 22.8 28.8H9.2C7.6 28.8 6.6 28 6.4 26.4Z'),
      flat(P.amber, poly(6, 15, 26, 15, 24.94, 25, 7.06, 25)),
      flat(P.gold, poly(6, 15, 26, 15, 25.85, 16.6, 6.15, 16.6)),
      fill(P.foam, xf(rect(15.6, 10.8, 7.4, 7.4, 1.6), { deg: 14, cx: 19.3, cy: 14.5 }), { w: 1.3 }),
      line(polyline(8.2, 11, 9.4, 23.4), 1.8, '#ffffff', 0.6),
    ],
    { s: 1.1, cy: 18.9, dy: -2.9 },
  ),
  /** A mason jar of clear moonshine with a metal ring lid and "XXX" on a paper label. */
  moonshine: [
    fill(P.glass, MASON),
    flat(
      P.water,
      'M7.4 14.6C7.4 13.8 7.6 13.4 8 13H24C24.4 13.4 24.6 13.8 24.6 14.6V25.8C24.6 27.2 23.6 28.2 22.2 28.2H9.8C8.4 28.2 7.4 27.2 7.4 25.8Z',
      0.3,
    ),
    shine(rect(8, 14.2, 1.6, 11, 0.8), 0.6),
    fill(P.cream, rect(9.4, 16.2, 13.2, 8.6, 1), { w: 1.2 }),
    line(
      'M11 18.2L13.6 22.8M13.6 18.2L11 22.8M14.7 18.2L17.3 22.8M17.3 18.2L14.7 22.8M18.4 18.2L21 22.8M21 18.2L18.4 22.8',
      1.5,
    ),
    ...masonLid,
  ],
  /** An empty glass mason jar with a metal ring lid. */
  jar: [
    fill(P.glass, MASON),
    shine(rect(8.8, 14, 2.2, 11.6, 1.1), 0.75),
    shine(rect(21.6, 14.6, 1.6, 4.4, 0.8), 0.75),
    shine(rect(11.6, 25.4, 9, 1.6, 0.8), 0.5),
    ...masonLid,
  ],
  /** A tall lab jar full of glowing green goo with a little dark critter floating in it. */
  'specimen-jar': [
    fill(P.glass, rect(9, 8.4, 14, 20.8, 2.4)),
    flat(P.lime, rect(9.8, 11.4, 12.4, 17, 1.8)),
    flat(P.lemon, ellipse(16, 19.4, 4.6, 5.4), 0.6),
    flat(
      P.black,
      smooth(
        '12.4 17.6, 13 15, 15.4 13.8, 18 14.6, 19 17.2, 18.4 19.4, 19.8 21.4, 19.6 23.8, 17.6 25.4, 14.8 25.8, 13.4 24.8, 14.8 24.2, 16.8 23.4, 17 21.6, 15 20.6, 13 19.6',
      ),
    ),
    flat(P.lime, circle(14.6, 17.2, 0.9) + circle(17.2, 17.2, 0.9)),
    flat(P.white, circle(12.4, 13.6, 0.8) + circle(20, 24.6, 0.9) + circle(19.8, 13, 0.8), 0.8),
    shine(rect(10.4, 9.6, 1.4, 17.6, 0.7), 0.5),
    fill(P.charcoal, rect(7.8, 3.2, 16.4, 5.6, 1.4)),
    line('M9.6 5.6H22.4', 1.2, P.darkGray),
  ],
  /** A wine bottle of pink-red "Strawberry Hill" with a big strawberry on the label. */
  'strawberry-hill': [
    fill(
      '#f04d6e',
      'M13.6 6.2H18.4V11C18.4 12.8 23.4 13.6 23.4 17.2V27C23.4 28.4 22.4 29.4 21 29.4H11C9.6 29.4 8.6 28.4 8.6 27V17.2C8.6 13.6 13.6 12.8 13.6 11Z',
    ),
    fill(P.silver, rect(13, 2.4, 6, 4.6, 1)),
    flat(P.cream, rect(9.4, 17.4, 13.2, 10.2, 1.2)),
    fill(
      P.red,
      'M16 27C13.2 27 12 23.6 12.2 21.6C12.4 20 14.2 19.4 16 20C17.8 19.4 19.6 20 19.8 21.6C20 23.6 18.8 27 16 27Z',
      {
        w: 1.2,
      },
    ),
    flat(P.green, 'M16 21L13.2 19.2L15 19L16 17.4L17 19L18.8 19.2Z'),
    flat(
      P.lemon,
      ellipse(14.6, 22.6, 0.6, 0.8) + ellipse(17.4, 22.4, 0.6, 0.8) + ellipse(16, 24.6, 0.6, 0.8),
    ),
    shine(rect(14.6, 7.6, 1.2, 4, 0.6), 0.5),
    shine(rect(10, 15.4, 1.4, 1.4, 0.7), 0.5),
  ],
  /** A red strawberry with green leaves and seeds. */
  strawberry: [
    fill(
      P.red,
      'M16 29.2C10.2 29.2 4.8 22.4 4.8 16C4.8 11.8 8.2 9.4 11.8 9.8C13.4 10 14.6 10.6 16 10.6C17.4 10.6 18.6 10 20.2 9.8C23.8 9.4 27.2 11.8 27.2 16C27.2 22.4 21.8 29.2 16 29.2Z',
    ),
    flat(
      P.lemon,
      pairs(
        '10 15.6, 14.6 15.2, 19.4 15.4, 23.6 16.6, 8.8 20.2, 12.4 19.6, 17 19.4, 21.4 20.4, 14.4 23.8, 18.6 24.2, 11.6 24.4',
      )
        .map(([x, y]) => ellipse(x, y, 0.7, 1))
        .join(''),
    ),
    fill(
      P.green,
      'M16 8.4L12.4 5.8L13.4 9.2L8.2 9.8L12.6 11.6L10.8 14L16 12.6L21.2 14L19.4 11.6L23.8 9.8L18.6 9.2L19.6 5.8Z',
    ),
    fill(P.green, 'M14.8 8.8C14.8 6.4 15.4 4.4 17 2.8L18.8 4.4C17.6 5.6 17.2 7 17.2 8.8Z'),
    shine(ellipse(9.4, 15.4, 1.4, 2.4), 0.5),
  ],
  /** A 2-liter bottle of neon green Mtn Dewd with a red cap. */
  'mtn-dewd': [
    fill(
      P.lime,
      'M13.6 5.6H18.4V8.6C18.4 10.8 22.8 11.6 22.8 15V25.2C22.8 26.8 22.4 28.8 20.8 29C19.8 29.2 19.2 28.6 18.6 28.6C17.8 28.6 17 29.4 16 29.4C15 29.4 14.2 28.6 13.4 28.6C12.8 28.6 12.2 29.2 11.2 29C9.6 28.8 9.2 26.8 9.2 25.2V15C9.2 11.6 13.6 10.8 13.6 8.6Z',
    ),
    fill(P.glass, rect(12.4, 6.6, 7.2, 1.8, 0.9), { w: 1.2 }),
    fill(P.red, rect(13, 2.2, 6, 4.2, 1)),
    flat(P.darkGreen, rect(10, 16.8, 12, 6.8)),
    flat(P.red, poly(10, 22, 13.4, 19, 16, 21, 19, 18.4, 22, 21, 22, 22.6, 10, 22.6)),
    shine(rect(11.2, 13.6, 1.6, 2.4, 0.8), 0.6),
    shine(rect(11.2, 24.2, 1.6, 2.6, 0.8), 0.6),
  ],
  /** A tall black energy drink can with a big green lightning bolt. */
  'energy-drink': [
    fill(P.silver, 'M10.8 6.4C10.8 5 11.6 4.6 12.2 3.2H19.8C20.4 4.6 21.2 5 21.2 6.4Z'),
    fill(
      P.charcoal,
      'M10 7.6C10 6.6 10.6 6 11.6 6H20.4C21.4 6 22 6.6 22 7.6V27C22 28.2 21.2 29 20 29H12C10.8 29 10 28.2 10 27Z',
    ),
    flat(P.lime, poly(18.6, 8, 12.4, 18.4, 15.8, 18.4, 13.4, 27, 20.2, 15.2, 16.8, 15.2)),
    shine(rect(11.2, 8, 1.2, 18, 0.6), 0.25),
  ],
  /** A tiny energy-shot bottle, orange and red, a little rocket on the label. */
  'energy-shot': [
    fill(
      P.orange,
      'M13.2 8.2H18.8V9.4C18.8 10.6 24.6 11 24.6 15V26.2C24.6 28 23.4 29.2 21.6 29.2H10.4C8.6 29.2 7.4 28 7.4 26.2V15C7.4 11 13.2 10.6 13.2 9.4Z',
    ),
    fill(P.red, rect(11.6, 2.4, 8.8, 5.8, 1.6)),
    flat(P.yellow, rect(8.2, 15, 15.6, 11.6, 1.4)),
    ...moved(
      [
        flat(P.fire, 'M14 18.4H18L16 23.6Z'),
        fill(P.red, 'M13.6 15.6L10.8 19.4L11.2 20.8L13.6 19.4ZM18.4 15.6L21.2 19.4L20.8 20.8L18.4 19.4Z', {
          w: 1.2,
        }),
        fill(P.white, 'M16 8.6C18.8 10.8 19.6 14.4 18.9 19.2H13.1C12.4 14.4 13.2 10.8 16 8.6Z', { w: 1.2 }),
        flat(P.red, 'M16 9.6C17.2 10.6 18 11.8 18.4 13H13.6C14 11.8 14.8 10.6 16 9.6Z'),
        fill(P.sky, circle(16, 15.6, 1.4), { w: 1 }),
      ],
      { s: 0.72, deg: 40, dy: 4.8 },
    ),
  ],
  /** A crinkly red-and-yellow bag of pork rinds, a few puffy rinds spilling out. */
  'pork-rinds': [
    ...moved(
      [
        fill(
          P.red,
          'M5.6 4.4L7.2 3.2L8.8 4.4L10.4 3.2L12 4.4L13.6 3.2L15.2 4.4L16.8 3.2L18.4 4.4L20 3.2L21.6 4.4L22.6 3.6C23.6 10 23.6 19 22.6 25.6L21 26.8L19.4 25.6L17.8 26.8L16.2 25.6L14.6 26.8L13 25.6L11.4 26.8L9.8 25.6L8.2 26.8L6.6 25.6L5.4 26.4C4.4 19 4.4 10 5.6 4.4Z',
        ),
        flat(P.darkRed, rect(6.2, 5.4, 15.8, 1.6)),
        flat(P.yellow, ellipse(14, 15.4, 6.4, 5.6)),
        fill(P.pink, ellipse(14, 15.4, 3.6, 2.6), { w: 1.2 }),
        flat(P.darkRed, ellipse(12.6, 15.4, 0.7, 1) + ellipse(15.4, 15.4, 0.7, 1)),
      ],
      { deg: -8, dx: -1.6, dy: -0.8 },
    ),
    fill(P.beige, rind(19.2, 25.8, 1.15)),
    fill(P.beige, rind(25.2, 21.6, 1.15)),
    flat(
      P.tan,
      ellipse(18, 25.4, 1, 0.7) +
        ellipse(20.8, 26.8, 0.9, 0.6) +
        ellipse(25.8, 21, 1, 0.7) +
        ellipse(23.6, 22.6, 0.8, 0.6),
    ),
  ],
  /** Gas station sushi: two maki rolls in a clear clamshell tray, a neon orange price sticker. */
  sushi: [
    fill(P.glass, rect(2.6, 7.6, 26.8, 17.6, 3.6)),
    line(rect(4.6, 9.4, 22.8, 13.6, 2.4), 1.2, P.sky),
    ...[10.2, 21.8].flatMap((x, i) => [
      fill(P.black, `M${x - 4.8} 12.4V17.8A4.8 3 0 0 0 ${x + 4.8} 17.8V12.4Z`),
      fill(P.white, ellipse(x, 12.4, 4.8, 3)),
      flat(i ? P.green : P.orange, ellipse(x, 12.4, 2, 1.3)),
    ]),
    fill(P.fire, xf(rect(17.6, 20.4, 9.6, 5.8, 1.2), { deg: -8, cx: 22.4, cy: 23.3 })),
    line(xf('M19.8 22.4H25M19.8 24.4H23', { deg: -8, cx: 22.4, cy: 23.3 }), 1.2),
  ],
  /** A foil TV-dinner tray: fried chicken, green peas, mashed potatoes and a brownie. */
  'tv-dinner': [
    fill(P.silver, rect(2.4, 6.4, 27.2, 20.4, 2.8)),
    fill(
      P.lightGray,
      rect(4.2, 8.2, 7.2, 6.4, 1.4) +
        rect(12.4, 8.2, 7.2, 6.4, 1.4) +
        rect(20.6, 8.2, 7.2, 6.4, 1.4) +
        rect(4.2, 15.6, 23.6, 9.4, 1.6),
      { w: 1.2, stroke: P.gray },
    ),
    fill(
      P.green,
      circle(6.4, 10.2, 1.2) + circle(9.2, 10.4, 1.2) + circle(7.8, 12.6, 1.2) + circle(5.6, 12.8, 0.9),
      { w: 1 },
    ),
    fill(P.cream, smooth('13.6 13, 13.4 10.6, 15 9.4, 16.4 9.8, 18 9.4, 18.6 11.2, 18.4 13.2, 16 13.6'), {
      w: 1.2,
    }),
    flat(P.yellow, rect(15, 10.6, 2.2, 1.6, 0.4)),
    fill(P.darkBrown, rect(21.8, 9.4, 4.8, 4, 0.8), { w: 1.2 }),
    ...moved(drumstick, { deg: 90, s: 0.85, cx: 16, cy: 16, dx: -2.4, dy: 4.2 }),
  ],
  /** A slice of pepperoni pizza. */
  pizza: moved(
    [
      fill(P.yellow, 'M4.8 9Q16 6 27.2 9L16.8 28.8C16.4 29.6 15.6 29.6 15.2 28.8Z'),
      fill(P.ketchup, circle(11.6, 13.4, 2.4) + circle(19.8, 13.2, 2.4) + circle(15.8, 20.2, 2.2), {
        w: 1.2,
      }),
      fill(
        P.bun,
        'M3.2 9.2C3 6 6.4 4 9 4.4C12 3.2 20 3.2 23 4.4C25.6 4 29 6 28.8 9.2C28.6 10.6 27.4 11 26.2 10.4C20 8 12 8 5.8 10.4C4.6 11 3.4 10.6 3.2 9.2Z',
      ),
    ],
    { deg: -18, s: 0.95, dx: 2.1 },
  ),
  /** A red-and-white striped bucket of fried chicken, golden drumsticks sticking out. */
  'fried-chicken': [
    ...moved(drumstick, { deg: -30, s: 0.88, dx: -6.2, dy: -2.4 }),
    ...moved(drumstick, { deg: 30, s: 0.88, dx: 6.2, dy: -2.4 }),
    ...moved(drumstick, { deg: 4, s: 0.88, dx: 0.2, dy: -3.6 }),
    fill(P.red, 'M5.2 15.6H26.8L24.4 27.6C24.2 28.6 23.6 29.2 22.6 29.2H9.4C8.4 29.2 7.8 28.6 7.6 27.6Z'),
    flat(
      P.white,
      poly(9.6, 16.4, 13.8, 16.4, 14.4, 28.4, 11, 28.4) + poly(18.2, 16.4, 22.4, 16.4, 21, 28.4, 17.6, 28.4),
    ),
    fill(P.white, rect(4.2, 13.6, 23.6, 3.4, 1.2)),
  ],
  /** A rack of glazed BBQ ribs with grill marks. */
  ribs: moved(
    [
      fill(
        P.cream,
        pairs('7.6 -10, 12.6 -4, 17.6 3, 22.6 9')
          .map(([x, deg]) => xf('M14.6 18V24.6C14.6 27 17.4 27 17.4 24.6V18Z', { deg, cy: 18, dx: x - 16 }))
          .join(''),
      ),
      fill(
        P.rust,
        'M3.4 13.4C3.4 10 6 8.6 9.4 8.8C14.4 9.2 19 8.2 23.4 7.8C27 7.4 28.8 9.4 28.6 12.8L28.2 18.4C28 20.6 26.8 21.8 25.2 21.8C24 21.8 23.4 20.6 22.6 20.6C21.8 20.6 21.2 21.8 20.1 21.8C19 21.8 18.4 20.6 17.6 20.6C16.8 20.6 16.2 21.8 15.1 21.8C14 21.8 13.4 20.6 12.6 20.6C11.8 20.6 11.2 21.8 10.1 21.8C9 21.8 8.4 20.6 7.6 20.6C6.8 20.6 6.2 21.8 5.2 21.8C4 21.6 3.4 20.4 3.4 18.4Z',
      ),
      line('M7.6 12L10.2 18.6M13 11.6L15.6 18.2M18.4 11L21 17.6M23.6 10.4L26 16.6', 1.5, P.darkBrown),
      line('M6.6 11.4C12 10.8 18 10.4 25 9.8', 1.4, '#ffffff', 0.5),
    ],
    { deg: -14, dy: -0.8 },
  ),
  /** A glass 9x13 casserole dish with handles, a golden cheesy baked top and a wisp of steam. */
  casserole: [
    line(
      'M12.4 8.6C11 7.2 13.6 5.8 12.4 4.2C11.8 3.4 12.2 2.8 12.8 2.4M19.6 8.6C18.2 7.2 20.8 5.8 19.6 4.2C19 3.4 19.4 2.8 20 2.4',
      1.5,
      P.gray,
    ),
    fill(P.glass, rect(2.4, 12.6, 5.6, 3.8, 1.6) + rect(24, 12.6, 5.6, 3.8, 1.6)),
    fill(P.glass, 'M4.2 15.6H27.8L26.6 25.8C26.4 27.4 25.4 28.4 23.8 28.4H8.2C6.6 28.4 5.6 27.4 5.4 25.8Z'),
    flat(P.bun, poly(5.4, 19.8, 26.6, 19.8, 25.9, 25.6, 6.1, 25.6)),
    flat(P.gold, poly(5.1, 17, 26.9, 17, 26.6, 19.8, 5.4, 19.8)),
    fill(P.glass, rect(4, 9.4, 24, 8, 3)),
    flat(P.gold, rect(5.4, 10.6, 21.2, 5.6, 2)),
    flat(
      P.amber,
      ellipse(9.4, 13.6, 1.8, 1) +
        ellipse(15.8, 12.4, 1.6, 0.8) +
        ellipse(21.6, 13.8, 2, 1) +
        ellipse(13, 14.8, 1, 0.6),
    ),
    shine(rect(7, 21, 1.4, 4, 0.7), 0.6),
  ],
  /** An ear of corn, the green husk peeled back. */
  corn: moved(
    [
      fill(P.darkGreen, 'M13.6 20C13.2 23.8 14.6 27 16 29C17.4 27 18.8 23.8 18.4 20Z'),
      fill(P.yellow, 'M10.8 20.6V9C10.8 5.4 13.2 2.6 16 2.6C18.8 2.6 21.2 5.4 21.2 9V20.6Z'),
      line('M14.4 4.2V20M17.6 4.2V20M12 7.6H20M11.6 11H20.4M11.6 14.4H20.4M11.6 17.8H20.4', 1.2, P.amber),
      fill(P.green, 'M11 17.4C8.6 20 7 23.4 6.6 27.6C10 27 13 25.4 15.6 22.6Z'),
      fill(P.green, 'M21 17.4C23.4 20 25 23.4 25.4 27.6C22 27 19 25.4 16.4 22.6Z'),
      line('M10.6 21.2C9.6 22.8 9 24.4 8.8 25.8M21.4 21.2C22.4 22.8 23 24.4 23.2 25.8', 1.2, P.darkGreen),
    ],
    { deg: 36, s: 0.94, dx: 2.2, dy: -1.4 },
  ),
  /** A pink-frosted donut with sprinkles. */
  donut: [
    fill(P.bun, circle(16, 16.6, 12.8) + hole(16, 16.6, 3.8)),
    fill(
      P.pink,
      smooth(
        Array.from({ length: 16 }, (_, i) => {
          const a = (i / 16) * Math.PI * 2;
          const r = i % 2 ? 10.6 : 11.6;
          return [16 + r * Math.cos(a), 15.6 + r * Math.sin(a)];
        }).flat(),
      ) + circle(16, 15.8, 5.2),
    ),
    line('M10.6 10.4L12.2 9.4M20.6 9.6L22 10.8M23.8 17.4L23.2 19.2', 1.6, P.yellow),
    line('M15.4 7.6L17.2 7.8M8.4 15.4L8.8 17.2M19.6 22.6L21.2 21.6', 1.6, P.blue),
    line('M11.6 21.8L13.2 22.8M24 13.2L24.6 14.8M7.8 12L9.2 11.2', 1.6, P.white),
    line('M16.6 24L18.2 24.2M22.6 14.4L21.8 13', 1.6, P.lime),
  ],
  /** A butterscotch hard candy in a clear yellow wrapper with twisted ends. */
  butterscotch: moved(
    [
      fill(P.lemon, WRAPPER_END),
      fill(P.lemon, xf(WRAPPER_END, { flip: true })),
      line('M9.4 15.4L4.6 12.4M9.4 16.6L4.6 19.6M22.6 15.4L27.4 12.4M22.6 16.6L27.4 19.6', 1.2, P.gold),
      fill(P.lemon, ellipse(16, 16, 8, 7.2)),
      flat(P.amber, ellipse(16, 16, 6.6, 5.8)),
      shine(ellipse(13.4, 13.4, 2.4, 1.2), 0.7),
    ],
    { deg: -14 },
  ),
  /** A chocolate bar with its wrapper peeled partway back. */
  'candy-bar': moved(
    [
      fill(P.brown, rect(10.4, 2.6, 11.2, 15, 1.6)),
      line('M16 4V14.6M11.6 7.6H20.4M11.6 12.2H20.4', 1.3, P.darkBrown),
      fill(P.silver, 'M10 14L5.6 17.6L8.6 20.6L10 18.6Z'),
      fill(P.silver, 'M22 14L26.4 17.6L23.4 20.6L22 18.6Z'),
      fill(
        P.red,
        'M9.6 13.4L11.6 15.2L13.6 13.6L15.6 15.4L17.6 13.6L19.6 15.2L22.4 13.4V27.6C22.4 28.6 21.6 29.4 20.6 29.4H11.4C10.4 29.4 9.6 28.6 9.6 27.6Z',
      ),
      flat(P.yellow, rect(10.4, 19.8, 11.2, 4.4)),
    ],
    { deg: 28, s: 0.92, dx: 0.3 },
  ),
  /** Two peanuts in the shell. */
  peanuts: [
    ...moved(peanut, { deg: -32, s: 1.1, dx: -5.6, dy: -1.6 }),
    ...moved(peanut, { deg: 24, s: 1.1, dx: 5.8, dy: 1.4 }),
  ],
  /** A bumpy green dill pickle. */
  pickle: moved(
    [
      fill(
        P.green,
        smooth(
          '16 2.8, 19.4 3.8, 21 6.6, 20.4 9.2, 21.9 11.6, 20.5 14.2, 21.9 16.6, 20.5 19.2, 21.6 21.8, 20.6 25.4, 18.6 28.4, 16 29.2, 13.4 28.4, 11.4 25.4, 10.4 21.8, 11.5 19.2, 10.1 16.6, 11.5 14.2, 10.1 11.6, 11.6 9.2, 11 6.6, 12.6 3.8',
        ),
      ),
      flat(
        P.darkGreen,
        circle(14.2, 9.4, 0.8) +
          circle(17.8, 12.6, 0.8) +
          circle(14, 16.4, 0.8) +
          circle(18, 20.4, 0.8) +
          circle(14.6, 24, 0.8),
      ),
      shine(rect(12.4, 6.2, 1.6, 18, 0.8), 0.4),
    ],
    { deg: 40, s: 1.08 },
  ),
  /** A few pale blue ice cubes. */
  ice: [...cube(11, 12.4, 13), ...cube(19.6, 19.8, 14)],
  /** A red-and-white pack of smokes, flip top open, two cigarettes poking out. */
  smokes: [
    fill(P.red, poly(7.4, 10.2, 24.6, 10.2, 26.6, 5.4, 9.4, 5.4)),
    fill(P.white, rect(11, 3.4, 3.4, 10, 1) + rect(15.6, 5.6, 3.4, 8, 1)),
    flat(P.amber, rect(11.8, 4.2, 1.8, 2.6) + rect(16.4, 6.4, 1.8, 2.6)),
    fill(P.white, rect(6.4, 10, 19.2, 19.4, 1.4)),
    flat(P.red, poly(7.2, 10.8, 24.8, 10.8, 24.8, 19.4, 16, 15.2, 7.2, 19.4)),
  ],
} satisfies Record<string, IconDef>;
